import { useEffect, useState } from 'react'

import http from 'common/http'

/*
 * Every browser request for map data goes through this module. It reaches the
 * data one of two ways, chosen per tenant by the gis_proxy feature flag:
 *
 * - Directly from GeoServer, with the Authorization header GeoServer requires.
 *   The credential comes from GET /gis/access. It is one shared account that
 *   keeps anonymous traffic out; it does not limit what a signed-in user can
 *   read. OpenLayers' default loaders and a plain <img src> cannot attach the
 *   header, so they would be rejected.
 * - Through the GIS proxy (GIS_URL), which authorizes every request against the
 *   tenant in a session cookie and builds the GeoServer request itself. The
 *   cookie is minted from the Auth0 token by POST /gis/session. See
 *   deployment/geoserver/api-proxy-design.md.
 *
 * Components keep building GeoServer-style parameters; the helpers below turn
 * them into what the proxy accepts when the flag is on.
 */

const GIS_ACCESS_PATH = '/gis/access'
// After /gis/access fails, carry on without a header for this long before
// asking again, rather than once per map tile.
const FAILED_ACCESS_RETRY_MS = 60 * 1000
// Assigning an <img> a src that is not an image fires its error event, which is
// how OpenLayers learns that a tile or image failed.
export const FAILED_IMAGE_SRC = 'data:,'

interface GisAccessResponse {
  authorization: string | null
  expiresAt: string
}

interface CachedAccess {
  authorization: string | null
  refreshAt: number
}

let cached: CachedAccess | null = null
let pending: Promise<string | null> | null = null

const requestAccess = async (): Promise<string | null> => {
  try {
    const { data } = await http.get(GIS_ACCESS_PATH, {
      suppressErrorModal: true,
    })
    const { authorization, expiresAt } = data as GisAccessResponse
    cached = { authorization, refreshAt: Date.parse(expiresAt) }
  } catch (error) {
    console.error('Failed to fetch GeoServer access', error)
    cached = {
      authorization: cached?.authorization ?? null,
      refreshAt: Date.now() + FAILED_ACCESS_RETRY_MS,
    }
  }
  return cached.authorization
}

/**
 * The Authorization header value for GeoServer, or null when the environment
 * does not require one. Concurrent callers share a single request.
 */
export const getGisAuthorization = ({
  refresh = false,
}: { refresh?: boolean } = {}): Promise<string | null> => {
  if (!refresh && cached && Date.now() < cached.refreshAt) {
    return Promise.resolve(cached.authorization)
  }
  if (!pending) {
    pending = requestAccess().finally(() => {
      pending = null
    })
  }
  return pending
}

export const resetGisAccess = (): void => {
  cached = null
  pending = null
}

export const geoserverUrl = (path = ''): string => `${GEOSERVER_URL}${path}`

// Read through typeof so builds and tests without GIS_URL still load this module.
const configuredGisUrl = (): string =>
  typeof GIS_URL === 'undefined' ? '' : (GIS_URL ?? '')

export const gisUrl = (path = ''): string => `${configuredGisUrl()}${path}`

export const isGisUrl = (url: string | undefined | null): boolean =>
  typeof url === 'string' &&
  configuredGisUrl() !== '' &&
  url.startsWith(gisUrl())

export const isGeoserverUrl = (url: string | undefined | null): boolean =>
  (typeof url === 'string' && url.startsWith(geoserverUrl())) || isGisUrl(url)

let gisProxyEnabled = false

/**
 * Switch map requests between GeoServer and the GIS proxy. Set once from the
 * feature flags, before the first map layer is built: layers keep the URL they
 * were created with.
 */
export const setGisProxyEnabled = (enabled: boolean): void => {
  gisProxyEnabled = enabled
}

export const isGisProxyEnabled = (): boolean => gisProxyEnabled

// The session cookie lives 30 minutes (GIS_SESSION_TTL_SECONDS on the proxy).
// Renewing at 20 leaves room for a slow request and a clock that drifts.
const GIS_SESSION_REFRESH_MS = 20 * 60 * 1000
// How long the cookie lives once set: GIS_SESSION_TTL_SECONDS on the proxy.
const GIS_SESSION_LIFETIME_MS = 30 * 60 * 1000
const GIS_SESSION_PATH = '/session'
// Partner hosts call the region's main API origin, so the session request and
// every map request must send credentials cross-origin (design §6).
const WITH_CREDENTIALS = { withCredentials: true, suppressErrorModal: true }

let sessionRefreshAt = 0
let sessionExpiresAt = 0
let pendingSession: Promise<boolean> | null = null
// Set by endGisSession: no new session may start while logging out, or a tile
// request could mint a fresh cookie after the DELETE.
let sessionEnded = false
let sessionKeepAlive: ReturnType<typeof setInterval> | null = null
const renewalListeners = new Set<() => void>()

/**
 * Called after a session is renewed once the previous cookie had lapsed or been
 * refused: requests made in between failed with 401. Map data loaded through
 * gisFetch retries by itself; Cesium loads 3D imagery without it, so the 3D
 * view subscribes here to request its failed tiles again. Returns the
 * unsubscribe function.
 */
export const onGisSessionRenewed = (listener: () => void): (() => void) => {
  renewalListeners.add(listener)
  return () => {
    renewalListeners.delete(listener)
  }
}

// A tab hidden for longer than the cookie's lifetime comes back with a lapsed
// session, and the next keep-alive tick may be 20 minutes away.
const renewWhenVisible = () => {
  if (document.visibilityState === 'visible') void ensureGisSession()
}

// Cesium loads 3D imagery from the proxy by itself, so the cookie has to stay
// fresh even when nothing goes through gisFetch.
const keepSessionAlive = () => {
  if (sessionKeepAlive) return
  sessionKeepAlive = setInterval(() => {
    if (document.visibilityState === 'visible') {
      void ensureGisSession({ refresh: true })
    }
  }, GIS_SESSION_REFRESH_MS)
  document.addEventListener('visibilitychange', renewWhenVisible)
}

const startGisSession = async (refused: boolean): Promise<boolean> => {
  try {
    await http.post(gisUrl(GIS_SESSION_PATH), null, WITH_CREDENTIALS)
    const now = Date.now()
    const lapsed = refused || (sessionExpiresAt > 0 && now >= sessionExpiresAt)
    sessionRefreshAt = now + GIS_SESSION_REFRESH_MS
    sessionExpiresAt = now + GIS_SESSION_LIFETIME_MS
    keepSessionAlive()
    if (lapsed) {
      for (const listener of renewalListeners) listener()
    }
    return true
  } catch (error) {
    console.error('Failed to start a GIS session', error)
    sessionRefreshAt = Date.now() + FAILED_ACCESS_RETRY_MS
    return false
  }
}

/**
 * Make sure the GIS session cookie is set and fresh. Concurrent callers share a
 * single request. Resolves false when the session could not be started.
 */
export const ensureGisSession = ({
  refresh = false,
  refused = false,
}: { refresh?: boolean; refused?: boolean } = {}): Promise<boolean> => {
  if (sessionEnded) return Promise.resolve(false)
  if (!refresh && !refused && Date.now() < sessionRefreshAt) {
    return Promise.resolve(true)
  }
  if (!pendingSession) {
    pendingSession = startGisSession(refused).finally(() => {
      pendingSession = null
    })
  }
  return pendingSession
}

/**
 * Expire the GIS session cookie. Call before logging out.
 *
 * Whether this page started a session says nothing about the cookie: one can
 * survive a reload, or a POST /session still in flight can set one. So with the
 * proxy on, wait for any pending request and always send the DELETE.
 */
export const endGisSession = async (): Promise<void> => {
  if (!gisProxyEnabled) return
  sessionEnded = true
  const pending = pendingSession
  if (pending) await pending
  clearSessionState()
  try {
    await http.delete(gisUrl(GIS_SESSION_PATH), WITH_CREDENTIALS)
  } catch (error) {
    console.error('Failed to end the GIS session', error)
  }
}

const clearSessionState = (): void => {
  sessionRefreshAt = 0
  sessionExpiresAt = 0
  pendingSession = null
  if (sessionKeepAlive) clearInterval(sessionKeepAlive)
  sessionKeepAlive = null
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', renewWhenVisible)
  }
}

/** Forget all session state, including a finished logout. */
export const resetGisSession = (): void => {
  clearSessionState()
  sessionEnded = false
}

/**
 * `fetch` against the GIS proxy. A 401 means the session lapsed, so it is
 * renewed and the request retried once.
 */
const gisFetch = async (url: string, init: RequestInit): Promise<Response> => {
  const send = () => fetch(url, { ...init, credentials: 'include' })
  await ensureGisSession()
  const response = await send()
  if (response.status !== 401) return response
  return (await ensureGisSession({ refused: true })) ? send() : response
}

/**
 * The origin Cesium must trust to send the session cookie with its own image
 * requests: the proxy's, when it differs from the page's (partner hosts, local
 * development). Undefined when the proxy is off or same-origin.
 */
export const gisCrossOrigin = ():
  { host: string; port: number } | undefined => {
  if (!gisProxyEnabled || configuredGisUrl() === '') return undefined
  const url = new URL(gisUrl(), window.location.href)
  if (url.origin === window.location.origin) return undefined
  const defaultPort = url.protocol === 'https:' ? 443 : 80
  return { host: url.hostname, port: Number(url.port) || defaultPort }
}

/** Base URL for a WMS-shaped request: one GeoServer path, or a proxy endpoint. */
export const wmsEndpointUrl = (
  endpoint: 'wms' | 'boundaries' | 'outside-points',
): string => (gisProxyEnabled ? gisUrl(`/${endpoint}`) : geoserverUrl('/ows'))

/**
 * The legend variant an SLD URL selects relative to the layer's own legend:
 * `…_swath_yield.sld` against `…_swath.sld` is `yield`. Undefined for the
 * layer's own legend.
 */
export const legendVariant = (
  legendUrl: string | undefined | null,
  sldUrl: unknown,
): string | undefined => {
  if (typeof sldUrl !== 'string' || !legendUrl || sldUrl === legendUrl) {
    return undefined
  }
  const stem = legendUrl.replace(/\.sld$/, '')
  if (!sldUrl.startsWith(`${stem}_`) || !sldUrl.endsWith('.sld')) {
    console.warn('SLD is not a variant of the layer legend', sldUrl, legendUrl)
    return undefined
  }
  return sldUrl.slice(stem.length + 1, -'.sld'.length)
}

/**
 * Parameters for a harvest layer's GetMap, GetFeatureInfo or GetLegendGraphic.
 * Unchanged for GeoServer. For the proxy, the harvest filter and SLD URL are
 * replaced by HARVEST and LEGEND, which the proxy turns back into a filter and
 * an SLD it reads from the harvest's own layer record.
 *
 * SLD-styled GetMaps leave LAYERS out (GeoServer's LAYERS/SLD reconciliation
 * bug), but the proxy needs the layer to authorize it, so it is added here; the
 * proxy leaves it out of its own GeoServer request again.
 */
export const harvestWmsParams = (
  params: Record<string, unknown>,
  {
    harvestId,
    legendUrl,
    layerName,
  }: { harvestId: unknown; legendUrl?: string | null; layerName?: string },
): Record<string, unknown> => {
  if (!gisProxyEnabled) return params
  const translated: Record<string, unknown> = {}
  const keys = new Set<string>()
  for (const [key, value] of Object.entries(params)) {
    const upper = key.toUpperCase()
    if (upper === 'CQL_FILTER') continue
    if (upper === 'SLD') {
      const variant = legendVariant(legendUrl, value)
      if (variant) translated.LEGEND = variant
      continue
    }
    keys.add(upper)
    translated[key] = value
  }
  if (layerName && !keys.has('LAYERS') && !keys.has('LAYER')) {
    translated.LAYERS = layerName
  }
  translated.HARVEST = harvestId
  return translated
}

/** The harvest filter to add to a request: GeoServer only; the proxy adds its own. */
export const harvestFilterParams = (
  harvestId: unknown,
): Record<string, string> =>
  gisProxyEnabled ? {} : { CQL_FILTER: encodeURI(`harvest_id='${harvestId}'`) }

const withAuthorization = (
  headers: HeadersInit | undefined,
  authorization: string | null,
): Headers => {
  const merged = new Headers(headers)
  if (authorization) merged.set('Authorization', authorization)
  return merged
}

/**
 * `fetch` for map data. GIS proxy URLs go through gisFetch with the session
 * cookie. GeoServer URLs carry the access header; a 401 means the shared
 * credential was rotated, so the header is fetched again and the request
 * retried once.
 */
export const geoserverFetch = async (
  url: string,
  init: RequestInit = {},
): Promise<Response> => {
  if (isGisUrl(url)) return gisFetch(url, init)
  const send = (authorization: string | null) =>
    fetch(url, {
      mode: 'cors',
      ...init,
      headers: withAuthorization(init.headers, authorization),
    })

  const authorization = await getGisAuthorization()
  const response = await send(authorization)
  if (response.status !== 401) return response

  const refreshed = await getGisAuthorization({ refresh: true })
  return refreshed && refreshed !== authorization ? send(refreshed) : response
}

/**
 * Load a GeoServer image into an <img> through a blob URL, which is released
 * once the element has decoded it. Failures end on the element's error event,
 * so an OpenLayers tile or image never stays in LOADING.
 */
export const loadGeoserverImage = async (
  image: HTMLImageElement,
  src: string,
): Promise<void> => {
  try {
    const response = await geoserverFetch(src)
    if (!response.ok) {
      throw new Error(`GeoServer responded with ${response.status}`)
    }
    const objectUrl = URL.createObjectURL(await response.blob())
    const release = () => URL.revokeObjectURL(objectUrl)
    image.addEventListener('load', release, { once: true })
    image.addEventListener('error', release, { once: true })
    image.src = objectUrl
  } catch (error) {
    console.error('Failed to load GeoServer image', error)
    image.src = FAILED_IMAGE_SRC
  }
}

/**
 * `tileLoadFunction` for TileWMS and `imageLoadFunction` for ImageWMS: both
 * hand over a wrapper whose getImage() is the element to fill.
 */
export const geoserverLoadFunction = (
  target: { getImage: () => unknown },
  src: string,
): void => {
  void loadGeoserverImage(target.getImage() as HTMLImageElement, src)
}

/**
 * A src usable in <img> for `url`: GeoServer URLs are fetched with the access
 * header and exposed as a blob URL, anything else passes through unchanged.
 * Undefined while a GeoServer image is still loading.
 */
export const useGeoserverImageUrl = (
  url: string | undefined | null,
): string | undefined => {
  const needsFetch = isGeoserverUrl(url)
  const [objectUrl, setObjectUrl] = useState<string>()

  useEffect(() => {
    if (!needsFetch) return undefined

    let cancelled = false
    let created: string | undefined
    setObjectUrl(undefined)

    geoserverFetch(url as string)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`GeoServer responded with ${response.status}`)
        }
        return response.blob()
      })
      .then((blob) => {
        if (cancelled) return
        created = URL.createObjectURL(blob)
        setObjectUrl(created)
      })
      .catch((error) => {
        console.error('Failed to load GeoServer image', error)
      })

    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [url, needsFetch])

  return needsFetch ? objectUrl : (url ?? undefined)
}
